import React, { useState, useEffect, useCallback, useRef } from 'react';
import { openCertificate } from '@/lib/certificates';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Mail, ClipboardList, Award, AlertTriangle, Send, Download, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { formatDay } from './AssignMentorDialog';
import { PLATFORM_LABEL } from '@/pages/parent/ParentPlacements';

const MARK_PILL = {
  present: 'bg-emerald-500 text-white', absent: 'bg-red-500 text-white',
  excused: 'bg-amber-400 text-white',
};
const MARK = { present: 'P', absent: 'A', excused: 'E' };
const API_ROOT = process.env.REACT_APP_BACKEND_URL;

const EXCEPTION_NOTE = {
  pending_review: ['Needs your review', 'text-amber-700'],
  approved: ['Exception approved', 'text-emerald-600'],
  not_approved: ['Exception not approved', 'text-[#6B7280]'],
  closed: ['Closed with the withdrawal', 'text-[#9CA3AF]'],
};

export default function InternshipExecutionTab({ programId: lockedProgram, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [programId, setProgramId] = useState(lockedProgram || null);
  const [verifying, setVerifying] = useState(null);
  const [reviewing, setReviewing] = useState(null);
  const [deciding, setDeciding] = useState(null);
  const [busy, setBusy] = useState(false);
  const loadedOnce = useRef(false);

  const load = useCallback(async (pid, silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await api.get('/coordinator/internship-execution', { params: pid ? { program_id: pid } : {} });
      setData(r.data);
      if (r.data?.board?.program?.id) setProgramId(r.data.board.program.id);
      if (loadedOnce.current) onChanged?.();
      loadedOnce.current = true;
    } catch { setData(null); }
    if (!silent) setLoading(false);
  }, [onChanged]);
  useEffect(() => { load(lockedProgram); }, [load, lockedProgram]);

  const remind = async (row) => {
    setBusy(true);
    try {
      await api.post(`/coordinator/internship-execution/${row.id}/remind`);
      toast.success(`Reminder sent to ${row.parent_name || 'the family'}`);
      load(programId);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not send the reminder'); }
    setBusy(false);
  };

  if (loading) return <div className="text-center py-12"><Loader2 className="h-6 w-6 animate-spin mx-auto text-[#9CA3AF]" /></div>;
  const board = data?.board;
  if (!board || board.students.length === 0) {
    return <p className="text-center py-12 text-[#9CA3AF] text-sm max-w-md mx-auto leading-relaxed" data-testid="execution-empty">
      Confirmed placements appear here. Finalise a student on the Applications tab to start the internship workflow.
    </p>;
  }
  const s = board.summary;
  const readOnly = !!board.program.read_only;

  const metrics = readOnly
    ? [['Total placed', s.placements], ['Completed', s.completed],
      ...(s.partially_completed ? [['Partially completed', s.partially_completed]] : []),
      ...(s.not_completed ? [['Not completed', s.not_completed]] : []),
      ...(s.withdrawn ? [['Withdrawn', s.withdrawn]] : []),
      ['Certificates issued', s.certificates_issued]]
    : [['Total placed', s.placements], ['In progress', s.in_progress],
      ['Awaiting verification', s.awaiting_verification],
      ...(s.completed ? [['Completed', s.completed]] : []),
      ...(s.certificates_issued ? [['Certificates issued', s.certificates_issued]] : [])];

  const alerts = readOnly ? [] : [
    s.awaiting_parent_confirmation && `${s.awaiting_parent_confirmation} famil${s.awaiting_parent_confirmation === 1 ? 'y has' : 'ies have'} not confirmed yet`,
    s.instructions_pending && `${s.instructions_pending} joining instruction${s.instructions_pending === 1 ? '' : 's'} to review`,
    s.attendance_exceptions && `${s.attendance_exceptions} attendance exception${s.attendance_exceptions === 1 ? '' : 's'} still to review`,
    s.withdrawal_requests && `${s.withdrawal_requests} withdrawal request${s.withdrawal_requests === 1 ? '' : 's'}`,
  ].filter(Boolean);

  return (
    <div data-testid="execution-tab">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {!lockedProgram && (
          <Select value={programId || ''} onValueChange={v => load(v)}>
            <SelectTrigger className="h-9 w-[270px] rounded-lg border-gray-200 text-xs bg-white" data-testid="execution-program-select">
              <SelectValue placeholder="Programme" />
            </SelectTrigger>
            <SelectContent>
              {(data.programs || []).map(p => <SelectItem key={p.id} value={p.id} className="text-xs">{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="bg-white rounded-xl border border-[#E5E7EB] px-4 py-3 mb-3" data-testid="execution-summary">
        <div className="flex flex-wrap gap-x-8 gap-y-2">
          {metrics.map(([label, value]) => (
            <div key={label} data-testid={`execution-metric-${label.toLowerCase().replace(/ /g, '-')}`}>
              <p className="text-lg font-semibold text-[#1F2937] leading-none font-['Outfit']">{value}</p>
              <p className="text-[11px] text-[#6B7280] mt-1">{label}</p>
            </div>
          ))}
        </div>
        {readOnly && (
          <p className="text-[11px] text-emerald-700 mt-2.5 flex items-start gap-1" data-testid="execution-readonly-note">
            <Lock className="h-3 w-3 mt-px shrink-0" />
            This programme is closed. Attendance, company feedback, school decisions and certificates are kept
            as a record and can no longer be changed.
          </p>
        )}
        {alerts.length > 0 && (
          <p className="text-[11px] text-amber-700 mt-2.5 flex items-start gap-1" data-testid="execution-alerts">
            <AlertTriangle className="h-3 w-3 mt-px shrink-0" />{alerts.join(' · ')}
          </p>
        )}
      </div>

      {board.instruction_reviews.length > 0 && (
        <div className="bg-white rounded-xl border border-[#E5E7EB] px-4 py-3 mb-3" data-testid="instruction-reviews">
          <p className="text-xs font-semibold text-[#1F2937] flex items-center gap-1.5 mb-2">
            <ClipboardList className="h-3.5 w-3.5 text-[#F97D6A]" />{readOnly ? 'Joining instructions sent' : 'Joining instructions'}
          </p>
          <ul className="divide-y divide-[#F3F4F6]">
            {board.instruction_reviews.map(x => (
              <li key={x.id} className="py-2 flex flex-wrap items-center justify-between gap-2" data-testid={`instruction-review-${x.id}`}>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-[#1F2937]">{x.title} <span className="text-[#9CA3AF]">· {x.company_name}</span></p>
                  <p className="text-[10px] text-[#9CA3AF]">
                    {x.interns} intern{x.interns === 1 ? '' : 's'} · reporting {formatDay(x.reporting_start_date)} ·
                    {' '}{(x.mode || '').replace('_', '-')} · {x.status.replace(/_/g, ' ')}
                    {x.has_unsent_changes ? ' · edited since families were told' : ''}
                  </p>
                </div>
                <div className="flex items-center gap-1.5">
                  <Badge className={`border-0 text-[10px] ${x.status === 'sent' ? 'bg-emerald-50 text-emerald-600' : x.status === 'approved' ? 'bg-sky-50 text-sky-600' : x.status === 'submitted' ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-500'}`}>
                    {x.status === 'sent' ? 'Shared with families' : x.status.replace(/_/g, ' ')}
                  </Badge>
                  <Button size="sm" variant="outline" onClick={() => setReviewing(x)} className="rounded-lg text-[11px] h-7 border-gray-200" data-testid={`review-instructions-${x.id}`}>{readOnly ? 'View' : 'Review'}</Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-x-auto" data-testid="execution-table">
        <table className="w-full text-left min-w-[960px]">
          <thead>
            <tr className="text-[10px] uppercase tracking-wide text-[#9CA3AF] border-b border-[#F3F4F6]">
              <th className="px-4 py-2.5 font-semibold">Student</th>
              <th className="px-3 py-2.5 font-semibold">Placement</th>
              <th className="px-3 py-2.5 font-semibold">Family</th>
              <th className="px-3 py-2.5 font-semibold">Attendance</th>
              <th className="px-3 py-2.5 font-semibold">Company note</th>
              <th className="px-3 py-2.5 font-semibold">School outcome</th>
              <th className="px-3 py-2.5 font-semibold text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {board.students.map(r => (
              <tr key={r.id} className="border-b border-[#F7F8F9] last:border-0 hover:bg-[#FAFBFC]" data-testid={`execution-row-${r.id}`}>
                <td className="px-4 py-3 align-top">
                  <p className="text-sm font-semibold text-[#1F2937]">{r.student_name}</p>
                  <p className="text-[11px] text-[#9CA3AF]">Grade {r.grade}</p>
                </td>
                <td className="px-3 py-3 align-top">
                  <p className="text-[11px] font-medium text-[#1F2937]">{r.title}</p>
                  <p className="text-[10px] text-[#6B7280]">{r.company_name} · {formatDay(r.start_date)}</p>
                </td>
                <td className="px-3 py-3 align-top">
                  <Badge className={`border-0 text-[10px] ${r.withdrawn ? 'bg-gray-100 text-gray-500' : r.participation.status === 'confirmed' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-700'}`} data-testid={`participation-${r.id}`}>
                    {r.withdrawn ? 'Withdrawn' : r.participation.status === 'confirmed' ? 'Confirmed' : 'Awaiting confirmation'}
                  </Badge>
                  {r.participation.confirmed_at && <p className="text-[10px] text-[#9CA3AF] mt-1">{r.participation.confirmed_by} · {formatDay(r.participation.confirmed_at.slice(0, 10))}</p>}
                  {r.participation.status !== 'confirmed' && !r.withdrawn && !readOnly && (
                    <button onClick={() => remind(r)} disabled={busy} className="text-[10px] text-[#F97D6A] hover:underline mt-1 inline-flex items-center gap-1" data-testid={`remind-${r.id}`}>
                      <Mail className="h-3 w-3" />Remind{r.participation.reminders_sent ? ` (${r.participation.reminders_sent} sent)` : ''}
                    </button>
                  )}
                  {r.withdrawal && (
                    <p className={`text-[10px] mt-1 ${r.withdrawal.status === 'requested' ? 'text-amber-700' : 'text-[#9CA3AF]'}`} data-testid={`withdrawal-${r.id}`}>
                      Withdrawal {r.withdrawal.status}
                      {r.withdrawal.status === 'requested' && !readOnly && (
                        <button onClick={() => setDeciding(r)} className="ml-1 underline" data-testid={`decide-withdrawal-${r.id}`}>review</button>
                      )}
                    </p>
                  )}
                </td>
                <td className="px-3 py-3 align-top">
                  <div className="flex items-center gap-1">
                    {r.attendance.days.map(d => (
                      <span key={d.day} title={`${formatDay(d.day)}${d.note ? ` · ${d.note}` : ''}`} className={`h-5 w-5 rounded text-[10px] font-bold flex items-center justify-center ${d.status ? MARK_PILL[d.status] : 'bg-[#F3F4F6] text-[#9CA3AF]'}`}>
                        {d.status ? MARK[d.status] : '·'}
                      </span>
                    ))}
                    <span className="text-[11px] text-[#6B7280] ml-1" data-testid={`attendance-${r.id}`}>{r.attendance.summary}</span>
                  </div>
                  {r.attendance.exception_state && (
                    <p className={`text-[10px] mt-0.5 flex items-center gap-1 ${EXCEPTION_NOTE[r.attendance.exception_state][1]}`} data-testid={`attendance-exception-${r.id}`}>
                      {r.attendance.exception_state === 'pending_review' && <AlertTriangle className="h-3 w-3" />}
                      {EXCEPTION_NOTE[r.attendance.exception_state][0]}
                    </p>
                  )}
                </td>
                <td className="px-3 py-3 align-top">
                  {r.completion.company_status ? (
                    <>
                      <p className="text-[11px] text-[#1F2937] capitalize">{r.completion.company_status.replace(/_/g, ' ')}</p>
                      <p className="text-[10px] text-[#6B7280]">{r.completion.conduct === 'satisfactory' ? 'Conduct satisfactory' : 'Conduct needs attention'}</p>
                      {r.completion.comments && <p className="text-[10px] text-[#6B7280] mt-0.5">“{r.completion.comments}”</p>}
                    </>
                  ) : <span className="text-[11px] text-[#9CA3AF]">Awaiting company</span>}
                </td>
                <td className="px-3 py-3 align-top">
                  {r.completion.school_status ? (
                    <>
                      <Badge className={`border-0 text-[10px] capitalize ${r.completion.school_status === 'completed' ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-500'}`} data-testid={`school-status-${r.id}`}>
                        {r.completion.school_status.replace(/_/g, ' ')}
                      </Badge>
                      <p className="text-[10px] text-[#9CA3AF] mt-1">{r.completion.verified_by} · {formatDay(r.completion.verified_at?.slice(0, 10))}</p>
                      {r.completion.verification_reason && <p className="text-[10px] text-[#6B7280]">“{r.completion.verification_reason}”</p>}
                    </>
                  ) : <span className="text-[11px] text-[#9CA3AF]" data-testid={`school-status-${r.id}`}>Not verified</span>}
                </td>
                <td className="px-3 py-3 align-top text-right whitespace-nowrap">
                  {r.can_verify && !readOnly && (
                    <Button size="sm" onClick={() => setVerifying(r)} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-lg text-[11px] h-7" data-testid={`verify-${r.id}`}>Verify</Button>
                  )}
                  {r.certificate && (
                    <button onClick={() => openCertificate(r.certificate.url)} className="text-[11px] text-[#F97D6A] hover:underline inline-flex items-center gap-1" data-testid={`certificate-${r.id}`}>
                      <Download className="h-3 w-3" />{r.certificate.number}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {verifying && <VerifyDialog row={verifying} onClose={() => setVerifying(null)} onDone={() => { setVerifying(null); load(programId); }} />}
      {reviewing && <ReviewDialog item={reviewing} readOnly={readOnly} onClose={() => setReviewing(null)} onRefresh={(updated) => { if (updated) setReviewing(r => ({ ...r, ...updated })); load(programId, true); }} onDone={() => { setReviewing(null); load(programId); }} />}
      {deciding && <WithdrawalDialog row={deciding} onClose={() => setDeciding(null)} onDone={() => { setDeciding(null); load(programId); }} />}
    </div>
  );
}

function VerifyDialog({ row, onClose, onDone }) {
  const [status, setStatus] = useState(row.completion.company_status || 'completed');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const overriding = status !== row.completion.company_status;
  const unfinished = row.attendance.pending_days > 0;
  const needsReason = overriding || (status === 'completed' && (row.attendance.has_exception || unfinished));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.post(`/coordinator/internship-execution/${row.id}/verify`, { status, reason: reason.trim() || null });
      toast.success(r.data.certificate ? `Completed · certificate ${r.data.certificate.number} issued` : 'Outcome recorded');
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not record this outcome'); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg rounded-2xl max-h-[88vh] overflow-y-auto" data-testid="verify-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-['Outfit']">Completion review · {row.student_name}</DialogTitle>
          <DialogDescription className="text-xs">{row.title} · {row.company_name}</DialogDescription>
        </DialogHeader>
        <ul className="text-[11px] text-[#4B5563] space-y-1 bg-[#FAFBFC] rounded-xl px-3 py-2.5" data-testid="verify-context">
          <li>Family acknowledgement: {row.participation.status === 'confirmed' ? `confirmed ${formatDay(row.participation.confirmed_at?.slice(0, 10))}` : 'not confirmed'}</li>
          <li>Joining instructions: {row.joining_instructions.status === 'sent' ? 'approved and shared' : row.joining_instructions.status.replace(/_/g, ' ')}</li>
          <li>Attendance: {row.attendance.summary}{row.attendance.has_exception ? ' · includes an absence or excused day' : ''}
            {row.attendance.pending_days > 0 && (
              <span className="text-amber-700">{' '}· {row.attendance.pending_days} day{row.attendance.pending_days === 1 ? '' : 's'} still unmarked
                {row.attendance.days_to_come > 0 ? ` (${row.attendance.days_to_come} not yet due)` : ''}</span>
            )}</li>
          <li>Company recommendation: {(row.completion.company_status || '').replace(/_/g, ' ')} · {row.completion.conduct === 'satisfactory' ? 'conduct satisfactory' : 'conduct needs attention'}</li>
          {row.completion.comments && <li>Supervisor: “{row.completion.comments}”</li>}
        </ul>
        <div>
          <Label className="text-xs font-medium text-[#1F2937]">School's decision</Label>
          <div className="flex gap-1.5 mt-1.5">
            {[['completed', 'Completed'], ['partially_completed', 'Partially completed'], ['not_completed', 'Not completed']].map(([v, l]) => (
              <button key={v} onClick={() => setStatus(v)} className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border ${status === v ? 'bg-[#1F2937] text-white border-[#1F2937]' : 'bg-white text-[#6B7280] border-gray-200'}`} data-testid={`verify-status-${v}`}>{l}</button>
            ))}
          </div>
        </div>
        <div>
          <Label className="text-xs font-medium text-[#1F2937]">Reason {needsReason ? '*' : '(optional)'}</Label>
          <Textarea value={reason} onChange={e => setReason(e.target.value)} placeholder={needsReason ? 'Required: you are overriding the company, or closing this while days are unmarked or still to come' : 'Recorded with your name'} className="mt-1 rounded-xl border-gray-200 min-h-[56px] text-sm resize-none" data-testid="verify-reason" />
        </div>
        {status === 'completed' && (
          <p className="text-[11px] text-emerald-700 flex items-center gap-1.5" data-testid="verify-certificate-note">
            <Award className="h-3.5 w-3.5" />The school certificate is issued and emailed to the family straight away.
          </p>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-xl border-gray-200">Cancel</Button>
          <Button onClick={save} disabled={saving || (needsReason && !reason.trim())} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl font-semibold disabled:opacity-40" data-testid="verify-confirm-btn">
            {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}Record outcome
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReviewDialog({ item: initial, readOnly, onClose, onDone, onRefresh }) {
  const [item, setItem] = useState(initial);
  const [note, setNote] = useState('');
  const [link, setLink] = useState(item.communication_link || '');
  const [saving, setSaving] = useState(false);

  const act = async (decision) => {
    setSaving(true);
    try {
      const r = await api.post(`/coordinator/joining-instructions/${item.id}/review`,
        { decision, note: note.trim() || null, communication_link: link.trim() || null });
      const n = r.data.emails?.sent ?? 0;
      toast.success(decision === 'send'
        ? `Sent to ${n} famil${n === 1 ? 'y' : 'ies'}`
        : decision === 'approve' ? 'Approved — you can send these to families now' : 'Changes requested');
      if (decision === 'approve') { setItem({ ...item, ...r.data }); onRefresh({ ...item, ...r.data }); } else { onDone(); }
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not update these instructions'); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg rounded-2xl max-h-[88vh] overflow-y-auto" data-testid="review-instructions-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-['Outfit']">{item.title}</DialogTitle>
          <DialogDescription className="text-xs">{item.company_name} · {item.interns} confirmed intern(s)</DialogDescription>
        </DialogHeader>
        <ul className="text-[11px] text-[#4B5563] space-y-1 bg-[#FAFBFC] rounded-xl px-3 py-2.5" data-testid="instructions-detail">
          <li>Reporting: {formatDay(item.reporting_start_date)}{item.reporting_end_date ? ` – ${formatDay(item.reporting_end_date)}` : ''}</li>
          <li>Timing: {item.daily_start_time || '—'} – {item.daily_end_time || '—'} · {(item.mode || '').replace('_', '-')}</li>
          <li>Where to report: {item.reporting_location || '—'}</li>
          <li>Address: {item.workplace_address || '—'}</li>
          <li>Supervisor: {item.supervisor_name || '—'}{item.supervisor_contact ? ` · ${item.supervisor_contact}` : ''}</li>
          <li>Bring: {item.items_to_bring || '—'}{item.dress_code ? ` · dress code: ${item.dress_code}` : ''}</li>
          {item.agenda && <li>Agenda: {item.agenda}</li>}
          {item.additional_instructions && <li>Also: {item.additional_instructions}</li>}
        </ul>
        {(item.meeting_link || (item.day_links || []).length > 0) && (
          <ul className="text-[11px] text-[#2A4B8D] space-y-1 bg-[#F7FAFF] border border-[#DCE7FB] rounded-xl px-3 py-2.5" data-testid="instructions-online-detail">
            <li className="font-semibold">Online joining ({PLATFORM_LABEL[item.meeting_platform] || 'meeting link'})</li>
            {item.meeting_link && (
              <li><a href={item.meeting_link} target="_blank" rel="noreferrer" className="underline break-all">{item.meeting_link}</a></li>
            )}
            {(item.day_links || []).map(d => (
              <li key={d.day}>{formatDay(d.day)}: <a href={d.link} target="_blank" rel="noreferrer" className="underline break-all">{d.link}</a></li>
            ))}
            {item.meeting_passcode && <li>Passcode: {item.meeting_passcode}</li>}
            {item.meeting_notes && <li>{item.meeting_notes}</li>}
          </ul>
        )}
        {['remote', 'hybrid'].includes(item.mode) && !item.meeting_link && (item.day_links || []).length === 0 && (
          <p className="text-[11px] text-amber-700" data-testid="instructions-missing-link">
            This internship has online days but no meeting link yet — ask the company to add one.
          </p>
        )}
        {!readOnly && (
          <div>
            <Label className="text-xs font-medium text-[#1F2937]">School communication link (optional)</Label>
            <input value={link} onChange={e => setLink(e.target.value)} placeholder="A school-approved group or helpline link to include" className="mt-1 w-full h-9 rounded-xl border border-gray-200 px-2.5 text-sm" data-testid="communication-link" />
          </div>
        )}
        {!readOnly && (
          <div>
            <Label className="text-xs font-medium text-[#1F2937]">Note to the company</Label>
            <Textarea value={note} onChange={e => setNote(e.target.value)} placeholder="Required when asking for changes" className="mt-1 rounded-xl border-gray-200 min-h-[56px] text-sm resize-none" data-testid="review-note" />
          </div>
        )}
        {readOnly ? (
          <DialogFooter>
            <p className="text-[11px] text-emerald-700 flex items-center gap-1 mr-auto" data-testid="instructions-readonly-note">
              <Lock className="h-3 w-3" />Shared with families{item.sent_at ? ` on ${formatDay(item.sent_at.slice(0, 10))}` : ''} — this programme is closed, so these cannot be changed.
            </p>
            <Button variant="outline" onClick={onClose} className="rounded-xl border-gray-200 text-xs" data-testid="close-instructions-btn">Close</Button>
          </DialogFooter>
        ) : (
          <DialogFooter className="gap-2 sm:justify-between">
            <Button variant="ghost" onClick={() => act('request_changes')} disabled={saving || !note.trim()} className="rounded-xl text-sm text-[#6B7280]" data-testid="request-changes-btn">Request changes</Button>
            <div className="flex gap-2">
              {item.status !== 'approved' && item.status !== 'sent' && (
                <Button variant="outline" onClick={() => act('approve')} disabled={saving} className="rounded-xl border-gray-200 text-xs" data-testid="approve-instructions-btn">Approve</Button>
              )}
              <Button onClick={() => act('send')} disabled={saving || !['approved', 'sent'].includes(item.status)} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl font-semibold disabled:opacity-40" data-testid="send-instructions-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}
                {item.status === 'sent' ? 'Resend update' : 'Send to parents'}
              </Button>
            </div>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

function WithdrawalDialog({ row, onClose, onDone }) {
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const act = async (decision) => {
    setSaving(true);
    try {
      await api.post(`/coordinator/internship-withdrawals/${row.id}`, { decision, note: note.trim() || null });
      toast.success(decision === 'approve' ? 'Withdrawal approved' : 'Request declined');
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not record this decision'); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md rounded-2xl" data-testid="withdrawal-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-['Outfit']">Withdrawal request · {row.student_name}</DialogTitle>
          <DialogDescription className="text-xs">{row.title} · {row.company_name}</DialogDescription>
        </DialogHeader>
        <p className="text-sm text-[#4B5563] bg-[#FAFBFC] rounded-xl px-3 py-2.5" data-testid="withdrawal-reason">
          “{row.withdrawal?.reason}” — {row.withdrawal?.requested_by}
        </p>
        <p className="text-[11px] text-[#9CA3AF]">
          Approving marks the student as withdrawn on the company's intern list and emails both sides. The seat is not
          reallocated automatically, and the placement, attendance and acceptance history are kept.
        </p>
        <div>
          <Label className="text-xs font-medium text-[#1F2937]">Note for the family</Label>
          <Textarea value={note} onChange={e => setNote(e.target.value)} className="mt-1 rounded-xl border-gray-200 min-h-[56px] text-sm resize-none" data-testid="withdrawal-note" />
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => act('reject')} disabled={saving} className="rounded-xl border-gray-200 text-xs" data-testid="reject-withdrawal-btn">Decline request</Button>
          <Button onClick={() => act('approve')} disabled={saving} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl font-semibold" data-testid="approve-withdrawal-btn">
            {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}Approve withdrawal
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
